'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import api from '../lib/api';

type User = {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: string;
  accessToken: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  avatar?: string;
};

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  loginWithToken: (token: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<boolean>;
  updateProfile: (profile: { name: string; phone?: string }) => Promise<User>;
};

interface ApiErrorShape {
  response?: { data?: { message?: string }; status?: number };
  message?: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
function isApiError(e: unknown): e is ApiErrorShape {
  return (
    isRecord(e) &&
    (typeof (e as { message?: unknown }).message === 'string' ||
      isRecord((e as { response?: unknown }).response))
  );
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // -------- cookie jwt
  const setAuthCookie = useCallback((token: string, maxAgeSeconds: number): void => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    const secure = window.location.protocol === 'https:';
    document.cookie = [
      `auth_token=${encodeURIComponent(token)}`,
      'Path=/',
      `Max-Age=${maxAgeSeconds}`,
      'SameSite=Lax',
      secure ? 'Secure' : '',
    ]
      .filter(Boolean)
      .join('; ');
  }, []);

  const clearAuthCookie = useCallback((): void => {
    if (typeof document === 'undefined') return;
    document.cookie = 'auth_token=; Path=/; Max-Age=0; SameSite=Lax';
  }, []);

  const logout = useCallback(() => {
    try {
      api.clearAuth();
      clearAuthCookie();
    } catch (err) {
      console.error('[AuthContext] Error during logout cleanup:', err);
    } finally {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('postLoginRedirect');
        setUser(null);
        window.location.href = '/login';
      }
    }
  }, [clearAuthCookie]);

  const refreshUser = useCallback(async (): Promise<boolean> => {
    try {
      // conservar accessToken actual
      let currentAccessToken = '';
      if (typeof window !== 'undefined') {
        const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
        currentAccessToken = currentUser?.accessToken || '';
      }

      // 👇 tu api.get devuelve el objeto User directamente (sin .data)
      const me = await api.get<User>('/users/me/profile');
      if (!me || typeof me.id !== 'string') {
        throw new Error('Invalid /users/me/profile response');
      }

      const normalized: User = {
        id: me.id,
        email: me.email,
        name: me.name || (typeof me.email === 'string' ? me.email.split('@')[0] : 'Usuario'),
        role: me.role,
        phone: me.phone || '',
        accessToken: currentAccessToken || me.accessToken || '',
        isActive: me.isActive ?? true,
        createdAt: me.createdAt || new Date().toISOString(),
        updatedAt: me.updatedAt || new Date().toISOString(),
        avatar: me.avatar,
      };

      // (opcional para depurar)
      console.log('[AuthContext] /users/me/profile ->', me);

      setUser(prev => (JSON.stringify(prev) === JSON.stringify(normalized) ? prev : normalized));

      if (typeof window !== 'undefined') {
        localStorage.setItem('user', JSON.stringify(normalized));
      }
      return true;
    } catch (error) {
      if (isApiError(error) && error.response?.status === 401) {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          api.clearAuth();
        }
        setUser(null);
        return false;
      }
      if (error instanceof Error) {
        console.error('[AuthContext] Refresh error:', error.message);
      }
      return false;
    }
  }, []);

  // ---------- init (sin redirecciones aquí)
  useEffect(() => {
    let isMounted = true;

    const initAuth = async (): Promise<void> => {
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
        if (token) {
          api.setAuthToken(token);
          const refreshed = await refreshUser();
          console.log('[AuthContext] User refresh result:', refreshed ? 'success' : 'failed');
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('[AuthContext] Error initializing auth:', error);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          api.clearAuth();
        }
        setUser(null);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    void initAuth();
    return () => {
      isMounted = false;
    };
  }, [refreshUser]);

  // ---------- login (DB)
  const login = useCallback(
    async (email: string, password: string) => {
      try {
        setIsLoading(true);

        if (!email || !password) {
          return { success: false, error: 'Email y contraseña son obligatorios' };
        }

        interface LoginResponse {
          access_token: string;
          user: User;
        }

        // tu api.post devuelve LoginResponse directamente
        const response = await api.post<LoginResponse>('/auth/login', { email, password });

        const token = response.access_token;
        const meFromLogin = response.user;

        if (!token) {
          return { success: false, error: 'Comprueba que el email y la contraseña son correctos.' };
        }

        api.setAuthToken(token);
        if (typeof window !== 'undefined') {
          localStorage.setItem('token', token);
          const maxAge = 60 * 60 * 8;
          setAuthCookie(token, maxAge);
        }

        // si el login no trae todo, pide el perfil completo (incluye phone)
        let userToSet = meFromLogin;
        if (!userToSet || !userToSet.phone) {
          userToSet = await api.get<User>('/users/me/profile');
        }

        const normalized: User = {
          ...userToSet,
          name:
            userToSet.name ||
            (typeof userToSet.email === 'string' ? userToSet.email.split('@')[0] : 'Usuario'),
          accessToken: token,
          isActive: userToSet.isActive ?? true,
          createdAt: userToSet.createdAt || new Date().toISOString(),
          updatedAt: userToSet.updatedAt || new Date().toISOString(),
        };

        setUser(normalized);

        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(normalized));
          const redirectTo = localStorage.getItem('postLoginRedirect') || '/';
          localStorage.removeItem('postLoginRedirect');
          if (window.location.pathname !== redirectTo) {
            window.location.href = redirectTo;
          }
        }

        return { success: true };
      } catch (error) {
        console.error('[AuthContext] Login error:', error);
        const msg =
          (isApiError(error) && (error.response?.data?.message ?? error.message)) ||
          (error instanceof Error ? error.message : 'Error de autenticación');

        try {
          api.clearAuth();
        } catch {}
        if (typeof window !== 'undefined') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }

        return { success: false, error: msg };
      } finally {
        setIsLoading(false);
      }
    },
    [setAuthCookie]
  );

  // ---------- login con token (JWT del backend)
  const loginWithToken = useCallback(
    async (token: string) => {
      try {
        if (!token) throw new Error('No authentication token provided');
        setIsLoading(true);

        api.setAuthToken(token);
        if (typeof window !== 'undefined') {
          localStorage.setItem('token', token);
          setAuthCookie(token, 60 * 60 * 8);
        }

        // usa el perfil completo para traer phone
        const me = await api.get<User>('/users/me/profile');
        if (!me || typeof me.id !== 'string') throw new Error('Failed to load user profile');

        const userWithDefaults: User = {
          ...me,
          name: me.name || (typeof me.email === 'string' ? me.email.split('@')[0] : 'Usuario'),
          isActive: me.isActive ?? true,
          createdAt: me.createdAt || new Date().toISOString(),
          updatedAt: me.updatedAt || new Date().toISOString(),
          accessToken: token,
        };

        setUser(userWithDefaults);
        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(userWithDefaults));
          const redirectTo = localStorage.getItem('postLoginRedirect') || '/';
          localStorage.removeItem('postLoginRedirect');
          if (window.location.pathname !== redirectTo) {
            window.location.href = redirectTo;
          }
        }

        return { success: true };
      } catch (error) {
        console.error('Login with token failed:', error);
        const errorMsg =
          (isApiError(error) && (error.response?.data?.message ?? error.message)) ||
          (error instanceof Error ? error.message : 'Login with token failed');
        return { success: false, error: errorMsg };
      } finally {
        setIsLoading(false);
      }
    },
    [setAuthCookie]
  );

  // ---------- UPDATE PROFILE ----------
  const updateProfile = useCallback(
    async (profileData: { name: string; phone?: string }) => {
      try {
        if (!user) throw new Error('User not authenticated');

        // 👇 tu api.patch devuelve el User directamente (sin .data)
        const updatedUser = await api.patch<User>('/users/me/profile', profileData);

        const mergedUser: User = {
          ...user,
          ...updatedUser,
          name: updatedUser.name || user.name,
          phone: updatedUser.phone ?? user.phone,
        };

        setUser(mergedUser);

        if (typeof window !== 'undefined') {
          localStorage.setItem('user', JSON.stringify(mergedUser));
        }

        return mergedUser;
      } catch (error) {
        console.error('Error updating profile:', error);
        throw error;
      }
    },
    [user]
  );

  const value: AuthContextType = {
    user,
    isAuthenticated: Boolean(user),
    isLoading,
    login,
    loginWithToken,
    logout,
    refreshUser,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
